import { useState, useRef, useEffect } from 'react';
import {
  Tag, Ruler, Palette, Hash, AppWindow, DoorOpen, Settings2, Scale, Microscope,
  RefreshCw, Pencil, Copy, Wand2, ChevronDown, ChevronUp,
} from 'lucide-react';
import FractionInput from './FractionInput';
import { WINDOW_TYPES } from '../../config/windowTypes';

const B = {
  brown:      '#8B4513',
  brownDark:  '#6B3410',
  brownLight: '#A0522D',
  cream:      '#faf7f4',
  creamDark:  '#f2ece4',
  creamBorder:'#e4d8cc',
  amber:      '#d97706',
  amberLight: '#fef3c7',
  text:       '#1c1008',
  textMid:    '#6b4c30',
  textLight:  '#9a7a60',
  white:      '#ffffff',
  red:        '#dc2626',
};

const FRAME_STYLES    = ['With Fin', 'With Fin & J', 'Without Fin'];
const COLORS_LIST     = ['White', 'Sandstone', 'Dk Woodgrain/White', 'Dk Woodgrain/Clay'];
const SCREEN_STYLES   = ['None', 'Flex Scrn', 'Std Scrn'];
const SCREEN_SIZES    = ['Full Scrn', 'Half Scrn'];
const MESH_TYPES_LIST = ['STD Mesh', 'BET Mesh', 'Alum Mesh'];
const GLASS_PACKAGES  = ['DC', 'DCA', 'DLA', 'TLA', 'TLK'];
const GLASS_OPTIONS   = ['None', 'Obscure', 'Gray Tint', 'Bronze Tint', 'Tempered'];
const GRID_PATTERNS   = ['None', 'Colonial', 'Standard Prairie', 'Spanned Prairie'];
const HANDING_LIST    = ['Hinge Left', 'Hinge Right', 'Fixed Unit', 'Awning'];
const GAS_FILLS_LIST  = ['Argon', 'Krypton', 'None'];

const COMBOS = {
  DH:    ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite', 'Q/H/Q'],
  HS:    ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite', 'Q/H/Q'],
  '3HS': ['Single'],
  PW:    ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite'],
  BSMT:  ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite'],
  CSMT:  ['Single', '2-Lite', '3-Lite', '4-Lite', '5-Lite', 'Q/H/Q'],
  BM:    ['Single'],
};

const PANEL_MAP = {
  DH:    ['Keeper', 'Lock'],
  HS:    ['Keeper'],
  '3HS': ['Keeper', 'Lock'],
  PW:    ['Picture'],
  BSMT:  ['Hopper'],
  CSMT:  ['Casement'],
  BM:    [],
};

function getGridTypes(pkg) {
  if (['TLA', 'TLK'].includes(pkg)) return ['None', 'Flat Grids'];
  return ['None', 'Flat Grids', 'Contour Grids'];
}

function getPatternDefaults(pattern) {
  if (pattern === 'Standard Prairie') return { v: '2', h: '2' };
  if (pattern === 'Spanned Prairie')  return { v: '2', h: '1' };
  return { v: '', h: '' };
}

const selectStyle = {
  width: '100%', padding: '9px 32px 9px 12px',
  border: `1.5px solid ${B.creamBorder}`,
  borderRadius: '8px', fontSize: '13px', color: B.text,
  background: B.white, outline: 'none', cursor: 'pointer',
  appearance: 'none', WebkitAppearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%238B4513' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
  boxSizing: 'border-box', fontFamily: 'inherit',
  transition: 'border-color 0.18s, box-shadow 0.18s',
};
const inputStyle = {
  width: '100%', padding: '9px 12px',
  border: `1.5px solid ${B.creamBorder}`,
  borderRadius: '8px', fontSize: '13px', color: B.text,
  background: B.white, outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit', transition: 'border-color 0.18s, box-shadow 0.18s',
};
const onFocus = e => { e.target.style.borderColor = B.brown; e.target.style.boxShadow = '0 0 0 3px rgba(139,69,19,0.12)'; };
const onBlur  = e => { e.target.style.borderColor = B.creamBorder; e.target.style.boxShadow = 'none'; };

function Field({ label, children, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', ...style }}>
      {label && (
        <label style={{ fontSize: '10px', fontWeight: '700', color: B.textMid, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

function StyledSelect({ label, value, onChange, options, style }) {
  return (
    <Field label={label} style={style}>
      <select value={value} onChange={e => onChange(e.target.value)} style={selectStyle} onFocus={onFocus} onBlur={onBlur}>
        {options.map(o => typeof o === 'string'
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

function StyledInput({ label, value, onChange, type = 'text', min, placeholder, style }) {
  return (
    <Field label={label} style={style}>
      <input
        type={type} min={min} value={value} placeholder={placeholder}
        onChange={e => onChange(type === 'number' ? (parseInt(e.target.value) || 0) : e.target.value)}
        style={inputStyle} onFocus={onFocus} onBlur={onBlur}
      />
    </Field>
  );
}

function FractionField({ label, value, onChange, placeholder, style }) {
  return (
    <Field label={label} style={style}>
      <FractionInput value={value} onChange={onChange} placeholder={placeholder} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
    </Field>
  );
}

function ChipGroup({ label, value, onChange, options }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
      {label && (
        <span style={{ fontSize: '10px', fontWeight: '700', color: B.textMid, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          {label}
        </span>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {options.map(opt => {
          const val    = typeof opt === 'string' ? opt : opt.value;
          const lbl    = typeof opt === 'string' ? opt : opt.label;
          const active = value === val;
          return (
            <button
              key={val} type="button" onClick={() => onChange(val)}
              style={{
                padding: '6px 14px', borderRadius: '20px',
                fontSize: '12.5px', fontWeight: active ? '700' : '500',
                cursor: 'pointer', fontFamily: 'inherit',
                border: `1.5px solid ${active ? B.brown : B.creamBorder}`,
                background: active ? B.brown : B.white,
                color: active ? B.white : B.textMid,
                boxShadow: active ? '0 2px 8px rgba(139,69,19,0.25)' : 'none',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
      border: `1.5px solid ${checked ? B.brown : B.creamBorder}`,
      background: checked ? B.amberLight : B.white,
      transition: 'all 0.18s ease', userSelect: 'none',
    }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ display: 'none' }} />
      <div style={{
        width: '36px', height: '20px', borderRadius: '10px', flexShrink: 0,
        background: checked ? B.brown : '#d1c4b8', position: 'relative', transition: 'background 0.2s',
      }}>
        <div style={{
          position: 'absolute', top: '3px', left: checked ? '19px' : '3px',
          width: '14px', height: '14px', borderRadius: '50%',
          background: B.white, boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 0.2s ease',
        }} />
      </div>
      <div>
        <div style={{ fontSize: '12.5px', fontWeight: '600', color: B.text }}>{label}</div>
        {description && <div style={{ fontSize: '10.5px', color: B.textLight }}>{description}</div>}
      </div>
    </label>
  );
}

function SectionHeader({ title, Icon, right, open, onToggle }) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: open ? '14px' : 0, paddingBottom: '9px',
        borderBottom: `1px solid ${B.creamBorder}`,
        cursor: onToggle ? 'pointer' : 'default', userSelect: 'none',
      }}
    >
      {Icon && <Icon size={14} strokeWidth={2} color={B.brown} />}
      <span style={{ fontSize: '11px', fontWeight: '700', color: B.brown, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{title}</span>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
        {right}
        {onToggle && (
          <ChevronDown size={14} strokeWidth={2.5} color={B.brown} style={{
            flexShrink: 0, transition: 'transform 0.18s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }} />
        )}
      </div>
    </div>
  );
}

const innerCard = { background: B.cream, borderRadius: '12px', padding: '16px 18px', border: `1px solid ${B.creamBorder}` };

function GlassPanel({ title, prefix, win, updateFn, isMirror, mirrorOn, onMirrorToggle }) {
  const pkg   = win[`${prefix}GlassPkg`]  ?? 'DLA';
  const opts  = win[`${prefix}GlassOpts`] ?? 'None';
  const gType = win[`${prefix}GridType`]  ?? 'None';
  const gPat  = win[`${prefix}GridPat`]   ?? 'None';
  const gV    = win[`${prefix}GridV`]     ?? '';
  const gH    = win[`${prefix}GridH`]     ?? '';

  function upd(field, val) { updateFn(win.id, `${prefix}${field}`, val); }

  function handlePattern(pat) {
    upd('GridPat', pat);
    const def = getPatternDefaults(pat);
    upd('GridV', def.v);
    upd('GridH', def.h);
  }

  return (
    <div style={{
      background: B.white, borderRadius: '12px', overflow: 'hidden',
      border: `1.5px solid ${B.creamBorder}`,
      boxShadow: '0 2px 12px rgba(139,69,19,0.07)',
      flex: 1,
    }}>
      <div style={{
        background: B.brown,
        padding: '10px 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ color: B.white, fontSize: '12.5px', fontWeight: '700', letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <AppWindow size={13} strokeWidth={2} /> {title}
        </span>
        {onMirrorToggle && (
          <button type="button" onClick={onMirrorToggle} style={{
            fontSize: '10px', padding: '3px 9px', borderRadius: '10px',
            background: mirrorOn ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
            color: B.white, border: '1px solid rgba(255,255,255,0.35)',
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            {mirrorOn ? <><RefreshCw size={10} strokeWidth={2} /> Mirrors Keeper</> : <><Pencil size={10} strokeWidth={2} /> Independent</>}
          </button>
        )}
        {isMirror && mirrorOn && (
          <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>Auto-synced</span>
        )}
      </div>

      <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <StyledSelect label="Glass Package" value={pkg}   onChange={v => upd('GlassPkg', v)}  options={GLASS_PACKAGES} />
        <StyledSelect label="Glass Options" value={opts}  onChange={v => upd('GlassOpts', v)} options={GLASS_OPTIONS} />
        <StyledSelect label="Grid Type"     value={gType} onChange={v => upd('GridType', v)}  options={getGridTypes(pkg)} />
        <StyledSelect label="Grid Pattern"  value={gPat}  onChange={handlePattern}             options={GRID_PATTERNS} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <Field label="Verticals">
            <input type="number" min={0} value={gV} onChange={e => upd('GridV', e.target.value)}
              style={{ ...inputStyle, textAlign: 'center' }} onFocus={onFocus} onBlur={onBlur} />
          </Field>
          <Field label="Horizontals">
            <input type="number" min={0} value={gH} onChange={e => upd('GridH', e.target.value)}
              style={{ ...inputStyle, textAlign: 'center' }} onFocus={onFocus} onBlur={onBlur} />
          </Field>
        </div>
      </div>
    </div>
  );
}

function CopyToAllButton({ onConfirm }) {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  function handleClick() {
    if (!armed) {
      setArmed(true);
      timerRef.current = setTimeout(() => setArmed(false), 2500);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    setArmed(false);
    onConfirm();
  }

  return (
    <button type="button" onClick={handleClick} style={{
      padding: '7px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
      background: armed ? B.amber : 'rgba(139,69,19,0.07)',
      color: armed ? B.white : B.brown,
      border: `1px solid ${armed ? B.amber : 'rgba(139,69,19,0.18)'}`,
      cursor: 'pointer', fontFamily: 'inherit',
      display: 'flex', alignItems: 'center', gap: '6px',
      transition: 'all 0.15s ease',
    }}>
      <Wand2 size={13} strokeWidth={2} /> {armed ? 'Confirm?' : 'Copy to All'}
    </button>
  );
}

export default function WindowRow({ window: win, index, onUpdate, onRemove, onDuplicate, onCopyToAll, collapsed, onToggleCollapse }) {
  const [lockMirror, setLockMirror] = useState(true);
  const [openSection, setOpenSection] = useState('identity');
  function toggleSection(key) {
    setOpenSection(cur => cur === key ? null : key);
  }

  const isCSMT        = win.type === 'CSMT';
  const is3LHS        = win.type === '3HS';
  const isDH          = win.type === 'DH';
  const threeHSLayout = win.threeHSLayout ?? 'qhq';
  const isEqualThirds = threeHSLayout === 'thirds';

  const combos = COMBOS[win.type] ?? ['Single'];

  const panels = (is3LHS && isEqualThirds)
    ? ['Keeper']
    : (PANEL_MAP[win.type] ?? []);

  const showCombination    = combos.length > 1;
  const showHandingSection = isCSMT;

  function update(id, field, val) {
    onUpdate(id, field, val);
    if (lockMirror && field.startsWith('Keeper')) {
      onUpdate(id, field.replace('Keeper', 'Lock'), val);
    }
  }

  const typeLabel = WINDOW_TYPES?.find(t => t.code === win.type)?.label ?? win.type;

  return (
    <div style={{
      background: B.white, borderRadius: '18px',
      border: `1px solid ${B.creamBorder}`,
      boxShadow: '0 4px 28px rgba(139,69,19,0.09), 0 1px 4px rgba(0,0,0,0.04)',
      marginBottom: '22px', overflow: 'hidden',
    }}>

      {/* Card header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px',
        background: 'rgba(139,69,19,0.03)',
        borderBottom: collapsed ? 'none' : `1px solid ${B.creamBorder}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '10px',
            background: B.brown,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: B.white, fontSize: '14px', fontWeight: '700',
            boxShadow: '0 3px 8px rgba(139,69,19,0.3)', flexShrink: 0,
          }}>{index + 1}</div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: B.text }}>
              Window {index + 1}
              {win.label && <span style={{ marginLeft: '8px', fontSize: '13px', color: B.textLight, fontWeight: '500' }}>— {win.label}</span>}
            </div>
            <div style={{ fontSize: '11px', color: B.textLight, marginTop: '2px' }}>
              {typeLabel}{win.config && win.config !== 'Single' ? ` · ${win.config}` : ''}{win.color ? ` · ${win.color}` : ''}
              {is3LHS && isEqualThirds && (
                <span style={{ marginLeft: '6px', color: '#b85000', fontWeight: '700' }}>· Equal Thirds</span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {onCopyToAll && <CopyToAllButton onConfirm={() => onCopyToAll(win.id)} />}
          <button type="button" onClick={() => onDuplicate(win.id)} style={{
            padding: '7px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
            background: 'rgba(139,69,19,0.07)', color: B.brown,
            border: '1px solid rgba(139,69,19,0.18)', cursor: 'pointer', fontFamily: 'inherit',
          }}>Duplicate</button>
          <button type="button" onClick={() => onRemove(win.id)} style={{
            padding: '7px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
            background: 'rgba(220,38,38,0.06)', color: B.red,
            border: '1px solid rgba(220,38,38,0.18)', cursor: 'pointer', fontFamily: 'inherit',
          }}>Remove</button>
          {onToggleCollapse && (
            <button type="button" onClick={onToggleCollapse} title={collapsed ? 'Expand' : 'Collapse'} style={{
              width: '30px', height: '30px', borderRadius: '8px',
              background: 'rgba(139,69,19,0.07)', color: B.brown,
              border: '1px solid rgba(139,69,19,0.18)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {collapsed ? <ChevronDown size={15} strokeWidth={2.5} /> : <ChevronUp size={15} strokeWidth={2.5} />}
            </button>
          )}
        </div>
      </div>

      {!collapsed && <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* 1. Identity */}
        <div style={innerCard}>
          <SectionHeader title="Identity" Icon={Tag} open={openSection === 'identity'} onToggle={() => toggleSection('identity')} />
          {openSection === 'identity' && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: '14px' }}>
              <StyledInput label="Label" value={win.label ?? ''} onChange={v => onUpdate(win.id, 'label', v)} placeholder="e.g. LR-1" />
              <StyledSelect label="Window Type" value={win.type} onChange={v => onUpdate(win.id, 'type', v)} options={WINDOW_TYPES.map(t => ({ value: t.code, label: t.label }))} />
              <StyledInput label="Qty" value={win.qty ?? 1} onChange={v => onUpdate(win.id, 'qty', parseInt(v) || 1)} type="number" min={1} />
            </div>
          )}
        </div>

        {/* 2. Overall Size */}
        <div style={{ ...innerCard, background: 'rgba(139,69,19,0.04)' }}>
          <SectionHeader title="Overall Size" Icon={Ruler} open={openSection === 'size'} onToggle={() => toggleSection('size')} />
          {openSection === 'size' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <FractionField label='OA Width'  value={win.width ?? ''}  onChange={v => onUpdate(win.id, 'width', v)}  placeholder='e.g. 23 1/2"' />
              <FractionField label='OA Height' value={win.height ?? ''} onChange={v => onUpdate(win.id, 'height', v)} placeholder='e.g. 49 1/2"' />
            </div>
          )}
        </div>

        {/* 3. Frame Style & Color */}
        <div style={innerCard}>
          <SectionHeader title="Frame Style & Color" Icon={Palette} open={openSection === 'frameColor'} onToggle={() => toggleSection('frameColor')} />
          {openSection === 'frameColor' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* ── DEFAULT: Without Fin ── */}
              <ChipGroup label="Frame Style" value={win.finType ?? 'Without Fin'} onChange={v => onUpdate(win.id, 'finType', v)} options={FRAME_STYLES} />
              <ChipGroup label="Color" value={win.color ?? 'White'} onChange={v => onUpdate(win.id, 'color', v)} options={COLORS_LIST} />
            </div>
          )}
        </div>

        {/* 4. Combination */}
        {showCombination && (
          <div style={innerCard}>
            <SectionHeader title="Combination" Icon={Hash} open={openSection === 'combination'} onToggle={() => toggleSection('combination')} />
            {openSection === 'combination' && (
              <ChipGroup label="" value={win.config ?? 'Single'} onChange={v => onUpdate(win.id, 'config', v)} options={combos} />
            )}
          </div>
        )}

        {/* 5. Screen — DEFAULTS: Flex Scrn / Full Scrn / STD Mesh */}
        <div style={innerCard}>
          <SectionHeader title="Screen" Icon={AppWindow} open={openSection === 'screen'} onToggle={() => toggleSection('screen')} />
          {openSection === 'screen' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <ChipGroup
                label="Screen Style"
                value={win.screenType ?? 'Flex Scrn'}
                onChange={v => onUpdate(win.id, 'screenType', v)}
                options={SCREEN_STYLES}
              />
              {(win.screenType ?? 'Flex Scrn') !== 'None' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <ChipGroup
                    label="Screen Size"
                    value={win.screenSize ?? 'Full Scrn'}
                    onChange={v => onUpdate(win.id, 'screenSize', v)}
                    options={SCREEN_SIZES}
                  />
                  <ChipGroup
                    label="Mesh Type"
                    value={win.meshType ?? 'STD Mesh'}
                    onChange={v => onUpdate(win.id, 'meshType', v)}
                    options={MESH_TYPES_LIST}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* 6. Casement options (CSMT only) */}
        {showHandingSection && (
          <div style={innerCard}>
            <SectionHeader title="Casement Options" Icon={DoorOpen} open={openSection === 'casement'} onToggle={() => toggleSection('casement')} />
            {openSection === 'casement' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                <ChipGroup label="Handing" value={win.handing ?? 'Hinge Left'} onChange={v => onUpdate(win.id, 'handing', v)} options={HANDING_LIST} />
                <StyledInput label="Mull Count" value={win.mullCount ?? 0} onChange={v => onUpdate(win.id, 'mullCount', parseInt(v) || 0)} type="number" min={0} />
                <StyledSelect label="Gas Fill" value={win.gasFill ?? 'Argon'} onChange={v => onUpdate(win.id, 'gasFill', v)} options={GAS_FILLS_LIST} />
              </div>
            )}
          </div>
        )}

        {/* 7. Additional Options */}
        <div style={innerCard}>
          <SectionHeader title="Additional Options" Icon={Settings2} open={openSection === 'additionalOptions'} onToggle={() => toggleSection('additionalOptions')} />
          {openSection === 'additionalOptions' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isCSMT ? '2fr' : '1fr 1fr 2fr', gap: '14px', marginBottom: '14px' }}>
                {!isCSMT && <StyledSelect label="Gas Fill" value={win.gasFill ?? 'Argon'} onChange={v => onUpdate(win.id, 'gasFill', v)} options={GAS_FILLS_LIST} />}
                {!isCSMT && <StyledInput label="Mull Count" value={win.mullCount ?? 0} onChange={v => onUpdate(win.id, 'mullCount', parseInt(v) || 0)} type="number" min={0} />}
                <StyledInput label="Options / Notes" value={win.options ?? ''} onChange={v => onUpdate(win.id, 'options', v)} placeholder="Special notes" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                <Toggle label="Night Latches" checked={win.nightLatches ?? false} onChange={v => onUpdate(win.id, 'nightLatches', v)} />
                <Toggle label="KD" description="Knocked Down" checked={win.kd ?? false} onChange={v => onUpdate(win.id, 'kd', v)} />
                <Toggle label="Insulated Frame" checked={win.insulatedFrame ?? false} onChange={v => onUpdate(win.id, 'insulatedFrame', v)} />
              </div>
            </>
          )}
        </div>

        {/* 7b. 3LHS Sash Layout */}
        {is3LHS && (
          <div style={{
            ...innerCard,
            border: isEqualThirds ? '1.5px solid rgba(180,80,0,0.3)' : `1px solid ${B.creamBorder}`,
            background: isEqualThirds ? 'rgba(255,242,224,0.5)' : B.cream,
          }}>
            <SectionHeader
              title="Sash Layout"
              Icon={Scale}
              open={openSection === 'sashLayout'}
              onToggle={() => toggleSection('sashLayout')}
              right={
                isEqualThirds
                  ? <span style={{ fontSize: '10px', fontWeight: '700', color: '#b85000', background: 'rgba(180,80,0,0.1)', padding: '2px 8px', borderRadius: '999px', border: '1px solid rgba(180,80,0,0.25)' }}>Equal Thirds Active</span>
                  : null
              }
            />
            {openSection === 'sashLayout' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <ChipGroup
                  label="Sash Configuration"
                  value={threeHSLayout}
                  onChange={v => onUpdate(win.id, 'threeHSLayout', v)}
                  options={[
                    { value: 'qhq',    label: 'Quarter / Half / Quarter' },
                    { value: 'thirds', label: 'Equal Thirds' },
                  ]}
                />
                <div style={{
                  fontSize: '11px',
                  color: isEqualThirds ? '#b85000' : B.textLight,
                  padding: '8px 12px', borderRadius: '8px',
                  background: isEqualThirds ? 'rgba(180,80,0,0.06)' : 'rgba(139,69,19,0.04)',
                  border: `1px solid ${isEqualThirds ? 'rgba(180,80,0,0.2)' : 'rgba(139,69,19,0.1)'}`,
                }}>
                  {isEqualThirds
                    ? 'Equal Thirds — all 3 sashes are the same width. One glass config applies to all 3 equal panes.'
                    : 'Quarter / Half / Quarter — default layout. Left and right sashes are ¼ of the overall width, center is ½.'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 7c. DH Sash Split — rare asymmetric option (Keeper top, Lock bottom) */}
        {isDH && (
          <div style={{
            ...innerCard,
            border: (win.sashSplit && win.sashSplit !== '50/50')
              ? '1.5px solid rgba(180,80,0,0.3)'
              : `1px solid ${B.creamBorder}`,
            background: (win.sashSplit && win.sashSplit !== '50/50')
              ? 'rgba(255,242,224,0.5)'
              : B.cream,
          }}>
            <SectionHeader
              title="Sash Split"
              Icon={Scale}
              open={openSection === 'sashSplit'}
              onToggle={() => toggleSection('sashSplit')}
              right={
                (win.sashSplit && win.sashSplit !== '50/50')
                  ? <span style={{
                      fontSize: '10px', fontWeight: '700', color: '#b85000',
                      background: 'rgba(180,80,0,0.1)', padding: '2px 8px',
                      borderRadius: '999px', border: '1px solid rgba(180,80,0,0.25)',
                    }}>
                      {win.sashSplit} Active
                    </span>
                  : null
              }
            />
            {openSection === 'sashSplit' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <ChipGroup
                  label="Top / Bottom Sash Ratio"
                  value={win.sashSplit ?? '50/50'}
                  onChange={v => onUpdate(win.id, 'sashSplit', v)}
                  options={[
                    { value: '50/50', label: '50/50 (Default)' },
                    { value: '25/75', label: '25/75 — Small Top' },
                    { value: '75/25', label: '75/25 — Small Bottom' },
                  ]}
                />
                <div style={{
                  fontSize: '11px',
                  color: (win.sashSplit && win.sashSplit !== '50/50') ? '#b85000' : B.textLight,
                  padding: '8px 12px', borderRadius: '8px',
                  background: (win.sashSplit && win.sashSplit !== '50/50')
                    ? 'rgba(180,80,0,0.06)' : 'rgba(139,69,19,0.04)',
                  border: `1px solid ${(win.sashSplit && win.sashSplit !== '50/50')
                    ? 'rgba(180,80,0,0.2)' : 'rgba(139,69,19,0.1)'}`,
                }}>
                  {(win.sashSplit ?? '50/50') === '50/50'
                    ? 'Standard — top and bottom sashes are equal halves. This is how every DH is built unless you change it.'
                    : 'Non-standard split — top and bottom sashes will be different heights. Glass, sash, and glazbead cuts adjust automatically. Verify cut list before the shop runs the job.'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 8. Glass Specification panels */}
        {panels.length > 0 && (
          <div style={innerCard}>
            <div
              onClick={() => toggleSection('glassSpec')}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: openSection === 'glassSpec' ? '12px' : 0, paddingBottom: '9px',
                borderBottom: `1px solid ${B.creamBorder}`,
                cursor: 'pointer', userSelect: 'none',
              }}
            >
              <Microscope size={14} strokeWidth={2} color={B.brown} />
              <span style={{ fontSize: '11px', fontWeight: '700', color: B.brown, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Glass Specification
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                {panels.length > 1 && (
                  <span style={{ fontSize: '11px', color: B.textLight }}>
                    {lockMirror ? 'Lock mirrors Keeper automatically' : 'Lock configured independently'}
                  </span>
                )}
                {is3LHS && isEqualThirds && (
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#b85000' }}>
                    Applies to all 3 equal panes
                  </span>
                )}
                <ChevronDown size={14} strokeWidth={2.5} color={B.brown} style={{
                  flexShrink: 0, transition: 'transform 0.18s ease',
                  transform: openSection === 'glassSpec' ? 'rotate(180deg)' : 'rotate(0deg)',
                }} />
              </div>
            </div>

            {openSection === 'glassSpec' && (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${panels.length}, 1fr)`, gap: '14px' }}>
                {panels.map((prefix, i) => (
                  <GlassPanel
                    key={prefix}
                    title={is3LHS && isEqualThirds ? 'Equal Glass — all 3 panes' : `${prefix} Glass`}
                    prefix={prefix}
                    win={win}
                    updateFn={update}
                    isMirror={i > 0}
                    mirrorOn={i > 0 ? lockMirror : null}
                    onMirrorToggle={i === 1 ? () => setLockMirror(s => !s) : null}
                  />
                ))}
              </div>
            )}
          </div>
        )}

      </div>}
    </div>
  );
}